import dayjs from 'dayjs';
import type { TaskStatus } from './task';
import { BaseType } from './model';
import { TransformStringToDayJS } from './utils';
import { User } from './user';
import { Type } from 'class-transformer';

export type AnalyticsTimeRange = '7d' | '30d' | 'ytd' | 'all';

export type AnalyticsInterval = 'weekly' | 'monthly' | 'all';

export interface SummaryStat {
  count: number;

  amount: number;
}

export interface AgingBalanceMonthly {
  months: string[];

  due: number[];
  days1_30: number[];
  days31_60: number[];
  days61_90: number[];
  days91Plus: number[];

  excluded_invoice_ids: string[];
}

export interface AgingBalanceSnapshot {
  due: number;

  days1_30: number;
  days31_60: number;
  days61_90: number;
  days91Plus: number;

  excluded_invoice_ids: string[];
}

export interface CashCollectedMonthly {
  amount: number;
  amounts: number[];
  months: string[];

  excluded_invoice_ids: string[];
}

export interface DsoMonthly {
  amount: number;
  dso: number[];
  months: string[];
  excluded_invoice_ids: string[];
}

export interface InvoiceBalanceSummary {
  due: SummaryStat;

  overdue: SummaryStat;

  total: SummaryStat;

  excluded: string[];
}

export interface EmailsSummary {
  total_inbound: number;

  total_outbound: number;

  total_automated: number;
}

export interface CollectionSummaryGroup {
  stuut: SummaryStat;

  total: SummaryStat;
}

export interface DateGroupedSummary<T> {
  all: T;

  seven_day: T;

  thirty_day: T;

  today: T;

  ytd: T;
}

export interface CollectionsSummary extends DateGroupedSummary<CollectionSummaryGroup> {}

export interface OutreachSummaryModalityDateGroup {
  automated: number;

  inbound: number;

  outbound: number;
}

export interface OutreachSummaryModalityGroup extends DateGroupedSummary<OutreachSummaryModalityDateGroup> {}

export interface OutreachSummary {
  calls: OutreachSummaryModalityGroup;

  emails: OutreachSummaryModalityGroup;
}

export class OutreachSummaryLine extends BaseType {
  @TransformStringToDayJS(true)
  period_end: dayjs.Dayjs;

  @TransformStringToDayJS(true)
  period_start: dayjs.Dayjs;

  avg_response_time: number;

  automated: number;

  generated: number;

  inbound: number;

  outbound: number;

  touched_by_stuut: number;

  @Type(() => EmailSummaryLine)
  calls: CallSummaryLine;

  @Type(() => EmailSummaryLine)
  emails: EmailSummaryLine;

  get emailAutomationRate(): number {
    return this.outbound ? Math.round((this.automated / this.outbound) * 100) : 0;
  }

  get avgResponseTimeDays(): number {
    return parseFloat((this.avg_response_time / 60).toFixed(2));
  }
}

export class CallSummaryLine extends BaseType {
  automated: number;

  avg_duration: number;

  connected: number;

  inbound: number;

  outbound: number;

  total: number;

  voicemail: number;

  get callAutomationRate(): number {
    return this.outbound ? Math.round((this.automated / this.outbound) * 100) : 0;
  }
}

export class EmailSummaryLine extends BaseType {
  automated: number;

  avg_response_time: number;

  generated: number;

  inbound: number;

  outbound: number;

  touched_by_stuut: number;

  get emailAutomationRate(): number {
    return this.outbound ? Math.round((this.automated / this.outbound) * 100) : 0;
  }
}

export class CollectionsSummaryLine extends BaseType {
  @TransformStringToDayJS(true)
  period_end: dayjs.Dayjs;

  @TransformStringToDayJS(true)
  period_start: dayjs.Dayjs;

  avg_days_to_pay_from_due: number;

  avg_days_to_pay_from_issued: number;

  amount_collected: number;

  amount_issued: number;

  amount_issued_stuut_touched: number;

  amount_collected_automated: number;

  amount_collected_stuut_touched: number;

  amount_collected_other: number;

  amount_overdue: number;

  amount_overdue_stuut_touched: number;

  amount_receivables: number;

  amount_receivables_stuut_touched: number;

  balance: number;

  balance_stuut_touched: number;

  balance_for_period: number;

  balance_period_start: number;

  balance_for_period_stuut_touched: number;

  dso: number | null;

  dso_stuut_touched: number | null;

  invoices_issued: number;

  invoices_open: number;

  invoices_overdue: number;

  invoices_paid: number;

  invoices_touched: number;

  invoices_touched_and_collected: number;

  invoices_touched_and_collected_ratio: number;

  total_invoice_amount: number;

  get overduePercentage(): number {
    return this.balance ? Math.round((this.amount_overdue / this.balance) * 100) : 0;
  }
}

export class CollectionsSummaryByCustomerLine extends CollectionsSummaryLine {
  partner_id: string;

  partner_name: string;

  partner_account_number: string | null;
}

export interface MonthlyDSO {
  period_end: dayjs.Dayjs;

  period_start: dayjs.Dayjs;

  dso: number;
}

export interface AgingBalance {
  days_0_count: number;

  days_1_30_count: number;

  days_31_60_count: number;

  days_61_90_count: number;

  days_91_plus_count: number;

  days_0_amount: number;

  days_1_30_amount: number;

  days_31_60_amount: number;

  days_61_90_amount: number;

  days_91_plus_amount: number;
}

export interface TaskStatusTotal {
  name: TaskStatus;

  total: number;
}

export interface TaskTypeTotal {
  name: string;

  total: number;
}

export class CollectorSummary extends BaseType {
  @TransformStringToDayJS(true)
  period_end: dayjs.Dayjs;

  @TransformStringToDayJS(true)
  period_start: dayjs.Dayjs;

  @Type(() => User)
  user: User;

  tasks_archived: number;

  tasks_closed: number;

  tasks_completed: number;

  total_tasks: number;

  tasks_open: number;

  emails_automated: number;

  emails_generated: number;

  emails_originated_in_stuut: number;

  emails_outbound: number;

  emails_touched_by_stuut: number;

  cash_collected: number;

  task_type_breakdown: Record<string, number>;
}

export class CustomerForecastLine extends BaseType {
  partner_id: string;

  partner_name: string;

  overdue_amount: number;

  overdue_percentage: number;

  due_week_1_amount: number;

  due_week_1_percentage: number;

  due_week_2_amount: number;

  due_week_2_percentage: number;

  total_receivables: number;
}

export interface InvoiceForecast {
  days_threshold: number;

  invoice_count: number;

  total_amount_due: number;
}

export interface MonthlyAgingSnapshotsInput {
  start_date: string; // YYYY-MM-DD format
  end_date: string; // YYYY-MM-DD format
  assignee_id?: string; // Optional collector/assignee ID
}

export interface MonthlyAgingSnapshotsOutput {
  months: string[]; // List of months in YYYY-MM format
  data: Record<string, number[]>; // Aging data by bucket (key = bucket name, value = array of values per month)
  totals: number[]; // Total aging balance for each month
  metadata: Record<string, any>; // Additional metadata
}

export class TaskSummaryLine extends BaseType {
  @TransformStringToDayJS(true)
  period_start: dayjs.Dayjs;

  @TransformStringToDayJS(true)
  period_end: dayjs.Dayjs;

  total_tasks: number;

  tasks_open: number;

  tasks_completed: number;

  tasks_closed: number;

  tasks_archived: number;

  tasks_failed: number;

  tasks_processing: number;

  tasks_assigned_to_agent: number;

  avg_completion_time_days: number;
}

export class TaskAutomationBreakdownLine extends BaseType {
  @TransformStringToDayJS(true)
  period_start: dayjs.Dayjs;

  @TransformStringToDayJS(true)
  period_end: dayjs.Dayjs;

  task_type: string;

  fully_automated: number;

  agent_assisted: number;

  total_completed: number;

  automation_rate: number;
}

export class TaskCompletionTimeByTypeLine extends BaseType {
  @TransformStringToDayJS(true)
  period_start: dayjs.Dayjs;

  @TransformStringToDayJS(true)
  period_end: dayjs.Dayjs;

  task_type: string;

  avg_completion_time_days: number | null;
}

export interface CollectorOutreachSummaryItem {
  collector_id: string;

  collector_name: string;

  emails_sent: number;

  automated_emails_sent: number;

  response_received: number;

  invoice_count: number;

  partner_count: number;

  response_count: number;

  // AR for all assigned invoices
  accounts_receivable_amount: number;

  accounts_receivable_collected_pct_by_invoice_count: number;

  accounts_receivable_collected_pct_by_invoice_amount: number;

  // AR for overdue invoices only
  overdue_invoices_count: number;

  overdue_accounts_receivable_amount: number;

  overdue_accounts_receivable_collected_pct_by_invoice_count: number;

  overdue_accounts_receivable_collected_pct_by_invoice_amount: number;
}

export interface BankTransactionBucket {
  count: number;
  total_amount: string;
}

export interface BankTransactionRecentMetrics {
  currency: string;
  is_multi_currency: boolean;
  needs_allocation: BankTransactionBucket;
  posting_errors_count: number;
  potential_payments: BankTransactionBucket;
  ready_to_post: BankTransactionBucket;
  total_count: number;
}
